import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Edit, Save, Trash2 } from "lucide-react";
import PhotoGallery from "@/components/app/PhotoGallery";

/**
 * Work Item card — two visual states (expanded editing / collapsed saved).
 *
 * props:
 *   reportId, item, index, isDraft
 *   expanded (bool)
 *   onSave(nextItem, {addAnother}) — persists (parent handles PATCH)
 *   onDelete()
 *   onCollapse(), onExpand()
 *   focusTitle (bool)              — expand + focus title (for fresh cards)
 */
export default function WorkItemCard({
    reportId,
    item,
    index,
    isDraft = true,
    expanded,
    onSave,
    onDelete,
    onCollapse,
    onExpand,
    focusTitle,
}) {
    const [draft, setDraft] = useState(item);
    const [errors, setErrors] = useState({});
    const titleRef = useRef(null);

    useEffect(() => {
        setDraft(item);
    }, [item]);

    useEffect(() => {
        if (expanded && focusTitle && titleRef.current) {
            titleRef.current.focus();
            titleRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [expanded, focusTitle]);

    const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    const validate = () => {
        const e = {};
        if (!(draft.title || "").trim()) e.title = "Title is required";
        if (!(draft.description_of_work || "").trim())
            e.description_of_work = "Description is required";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const doSave = (addAnother) => {
        if (!validate()) return;
        onSave(draft, { addAnother });
    };

    const onPhotosChange = (nextPhotos) => {
        // Photos are managed server-side. Reflect in local state; parent
        // will also patch its state via the same photo endpoint response.
        setDraft((d) => ({ ...d, photos: nextPhotos }));
    };

    // ---- Collapsed state ----
    if (!expanded) {
        const desc = (item.description_of_work || "").trim();
        return (
            <div
                className="glass-card !py-4"
                data-testid={`work-item-collapsed-${index}`}
            >
                <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                            <span className="text-[11px] uppercase tracking-wider text-slate-400">
                                #{index + 1}
                            </span>
                            <div
                                className="text-[15px] font-semibold text-slate-900 truncate"
                                data-testid={`work-item-title-view-${index}`}
                            >
                                {item.title || <span className="text-slate-400 italic">Untitled</span>}
                            </div>
                        </div>
                        {(item.location || "").trim() && (
                            <div className="text-[12px] text-slate-500 mt-0.5">
                                {item.location}
                            </div>
                        )}
                        {desc && (
                            <div className="text-[13px] text-slate-600 mt-1 line-clamp-1">
                                {desc}
                            </div>
                        )}
                        {(item.photos || []).length > 0 && (
                            <div className="text-[11px] text-slate-400 mt-1">
                                {(item.photos || []).length} photo
                                {(item.photos || []).length === 1 ? "" : "s"}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {isDraft && (
                            <>
                                <button
                                    type="button"
                                    onClick={onExpand}
                                    className="btn btn-secondary btn-sm"
                                    data-testid={`work-item-edit-${index}`}
                                >
                                    <Edit size={13} strokeWidth={2} />
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    onClick={onDelete}
                                    className="btn btn-danger btn-sm btn-icon"
                                    aria-label="Delete work item"
                                    data-testid={`work-item-delete-${index}`}
                                >
                                    <Trash2 size={13} strokeWidth={2} />
                                </button>
                            </>
                        )}
                        {!isDraft && (
                            <button
                                type="button"
                                onClick={onExpand}
                                className="btn btn-secondary btn-sm"
                                data-testid={`work-item-expand-${index}`}
                            >
                                <ChevronDown size={13} strokeWidth={2} />
                                Expand
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ---- Expanded state ----
    return (
        <div className="glass-card" data-testid={`work-item-expanded-${index}`}>
            <div className="flex items-start justify-between mb-4">
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-400">
                        Work item #{index + 1}
                    </div>
                    <div className="text-[15px] font-semibold text-slate-900 mt-0.5">
                        {draft.title || "New work item"}
                    </div>
                </div>
                {onCollapse && (
                    <button
                        type="button"
                        onClick={onCollapse}
                        className="btn btn-secondary btn-sm btn-icon"
                        aria-label="Collapse"
                        data-testid={`work-item-collapse-${index}`}
                    >
                        <ChevronUp size={13} strokeWidth={2} />
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="label label-required" htmlFor={`wi-title-${index}`}>
                        Title
                    </label>
                    <input
                        id={`wi-title-${index}`}
                        ref={titleRef}
                        className="input"
                        value={draft.title || ""}
                        onChange={(e) => setField("title", e.target.value)}
                        data-testid={`work-item-title-${index}`}
                    />
                    {errors.title && (
                        <div className="err" data-testid={`work-item-title-error-${index}`}>
                            {errors.title}
                        </div>
                    )}
                </div>
                <div>
                    <label className="label" htmlFor={`wi-location-${index}`}>
                        Location
                    </label>
                    <input
                        id={`wi-location-${index}`}
                        className="input"
                        value={draft.location || ""}
                        onChange={(e) => setField("location", e.target.value)}
                        data-testid={`work-item-location-${index}`}
                    />
                </div>
            </div>

            <div className="mt-4">
                <label className="label label-required" htmlFor={`wi-desc-${index}`}>
                    Description of work
                </label>
                <textarea
                    id={`wi-desc-${index}`}
                    rows={4}
                    className="textarea"
                    value={draft.description_of_work || ""}
                    onChange={(e) => setField("description_of_work", e.target.value)}
                    data-testid={`work-item-description-${index}`}
                />
                {errors.description_of_work && (
                    <div className="err" data-testid={`work-item-description-error-${index}`}>
                        {errors.description_of_work}
                    </div>
                )}
            </div>

            <div className="mt-4">
                <label className="label" htmlFor={`wi-mat-${index}`}>
                    Materials used
                </label>
                <textarea
                    id={`wi-mat-${index}`}
                    rows={3}
                    className="textarea"
                    value={draft.materials_used || ""}
                    onChange={(e) => setField("materials_used", e.target.value)}
                    data-testid={`work-item-materials-${index}`}
                />
            </div>

            <div className="mt-4">
                <label className="label">Photos</label>
                <PhotoGallery
                    reportId={reportId}
                    photos={draft.photos || []}
                    workItemId={draft.id}
                    readOnly={!isDraft || !draft.id}
                    onChange={onPhotosChange}
                    testIdPrefix={`work-item-photos-${index}`}
                />
                {!draft.id && (
                    <div className="text-[11px] text-slate-400 mt-1">
                        Save this work item before adding photos.
                    </div>
                )}
            </div>

            <div className="flex items-center justify-end gap-2 mt-6">
                <button
                    type="button"
                    onClick={onDelete}
                    className="btn btn-danger btn-sm"
                    data-testid={`work-item-delete-expanded-${index}`}
                >
                    <Trash2 size={13} strokeWidth={2} />
                    Delete
                </button>
                <button
                    type="button"
                    onClick={() => doSave(false)}
                    className="btn btn-secondary"
                    data-testid={`work-item-save-${index}`}
                >
                    <Save size={14} strokeWidth={2} />
                    Save
                </button>
                <button
                    type="button"
                    onClick={() => doSave(true)}
                    className="btn btn-primary"
                    data-testid={`work-item-save-and-add-${index}`}
                >
                    Save &amp; Add
                </button>
            </div>
        </div>
    );
}
